// @flow
import BaseModel from "../base/BaseModel";
import Href from "../href/Href";
import LinkModel from "../links/LinkModel";
import ContentTypeModel from "./ContentTypeModel";
import { TIMEVERSION_FILTER_NAME } from "../../constants/Constants";
import { getSetting } from "../../constants/Settings";

import LinkCollection from "../links/LinkCollection";

import type {
  ModularUIModel,
  IModelWithChildModels,
  ModelOptions,
  SectionFragment,
} from "../types";

/**
 * Link to a concept
 */
export default class ContentLinkModel
  extends BaseModel
  implements IModelWithChildModels
{
  _links: ?LinkCollection;
  _entryDate: ?ISO_DATE;
  _contentType: ContentTypeModel | null = null;
  _items: Array<ContentLinkModel>;

  /**
   */
  constructor(
    data: Object,
    entryDate: ?ISO_DATE = null,
    modelOptions?: ModelOptions,
  ) {
    super(data, {}, modelOptions);

    this._entryDate = entryDate;
  }

  /**
   */
  // $FlowFixMe[method-unbinding]
  getInitialChildModelLinks(): Array<LinkModel> {
    if (this.contentTypeLink) {
      this.contentTypeLink.isCacheable = true;
      return [this.contentTypeLink];
    }

    return [];
  }

  /**
   */
  // $FlowFixMe[method-unbinding]
  setChildModels(models: Array<ModularUIModel>) {
    if (this.contentTypeLink) {
      const href = this.contentTypeLink.href;
      const contentTypeModel = models.find((model) =>
        model.selfhref.equalsWithParameters(href),
      );

      if (contentTypeModel?.type === "ContentType") {
        this.contentType = contentTypeModel;
      }
    }
  }

  /**
   * Retrieve content type
   */
  get contentType(): ?ContentTypeModel | null {
    return this._contentType;
  }

  /**
   * Set concept type
   */
  set contentType(contentType: ?ModularUIModel) {
    this._contentType =
      contentType instanceof ContentTypeModel ? contentType : null;
  }

  /**
   */
  set entryDate(entryDate: ISO_DATE) {
    this._entryDate = entryDate;
  }

  /**
   * Retrieve key
   */
  get key(): string {
    return this.getData("_id", "");
  }

  /**
   * Retrieve label
   */
  get label(): string {
    return this.getData("label", "");
  }

  /**
   * Retrieve the label of the source a link to a section belongs to
   */
  get sourceLabel(): string | null {
    return this.getData("sourceLabel");
  }

  /**
   *
   */
  get abbreviation(): string | null {
    return this.getData("abbreviation") || this.getData("sourceAbbreviation");
  }

  /**
   */
  get section(): string | null {
    return this.data.section;
  }

  /**
   */
  createEncodedHref(): Href {
    const startURI = "/content/";
    const selfHref = new Href(
      this.data._links.self.href,
      "",
      this.modelOptions,
    );
    const href = selfHref.path;

    const sourceId = this.section
      ? href.substring(startURI.length, href.lastIndexOf("/"))
      : href.substring(startURI.length);

    if (this.section == null) {
      selfHref.path = `${startURI}${encodeURIComponent(sourceId)}`;
    } else {
      const encodedSection = encodeURIComponent(this.section);
      selfHref.path = `${startURI}${encodeURIComponent(sourceId)}/${encodedSection}`;
    }

    return selfHref;
  }

  /**
   * Encode the content-identifier of the path to the content resource.
   * This makes it a single uri part, which can be used on routes to make nested routes
   */
  get encodedHref(): Href {
    const href = this.createEncodedHref();

    if (this._entryDate !== null) {
      return href.addParameter(TIMEVERSION_FILTER_NAME, this._entryDate);
    }

    return href;
  }

  /**
   * Getting the links of the resource
   */
  get links(): LinkCollection {
    if (!this._links) {
      this._links = new LinkCollection(
        Array.isArray(this.data._links)
          ? this.data._links[0]
          : this.data._links,
        null,
        this.modelOptions,
      );
    }

    return this._links;
  }

  /**
   * Get self link of model
   */
  get selflink(): LinkModel {
    let selflink = this.links.getLinkByKey("self");

    if (getSetting("COMPLETE_SOURCE", false)) {
      if (this.links.hasLinkByKey("content")) {
        selflink = this.links.getLinkByKey("content");
      }

      if (
        this.section != null &&
        selflink &&
        !selflink.href.path.endsWith(encodeURIComponent(this.section))
      ) {
        selflink.href.addParameter("complete", "true");
        selflink.href.hash = this.section || "";
      }
    }

    if (selflink === null) {
      throw new Error(
        `Could not find self link for ${
          this.key === null ? "unknown" : this.key
        }`,
      );
    }

    if (selflink.href.state) {
      selflink.href.state.fragment = this.fragment;
    } else {
      selflink.href.state = {
        fragment: this.fragment,
      };
    }

    return selflink;
  }

  /**
   */
  get contentHref(): Href {
    let href = this.data._links.self.href;
    if (this.data._links.content) {
      href = new Href(this.data._links.content.href);
    } else if (this.section) {
      const section = encodeURIComponent(this.section);
      if (href.endsWith(section)) {
        href = href.substring(0, href.lastIndexOf("/" + section));
      }
    }

    return new Href(href)
      .addParameter("complete", "true")
      .addParameter(TIMEVERSION_FILTER_NAME, this._entryDate);
  }

  /**
   */
  get sectionHref(): Href | null {
    const section = this.section;
    if (section) {
      const contentHref = this.contentHref;
      const sectionHref = new Href(
        contentHref.path + "/" + encodeURIComponent(this.data.section),
      );
      return sectionHref.addParameter(TIMEVERSION_FILTER_NAME, this._entryDate);
    }
    return null;
  }

  /**
   * Self href of concept
   */
  get selfhref(): Href {
    if (this._entryDate !== null) {
      return this.selflink.href.addParameter(
        TIMEVERSION_FILTER_NAME,
        this._entryDate,
      );
    }

    return this.selflink.href;
  }

  /**
   */
  get subSectionID(): string {
    return this.selfhref.hash;
  }

  /**
   */
  get contentTypeLink(): LinkModel | null {
    return this.links.getLinkByKey("contenttype");
  }

  /**
   * Concept type href of concept
   */
  get contentTypeHref(): Href | null {
    if (this.contentTypeLink) {
      return this.contentTypeLink.href;
    }

    return null;
  }

  /**
   */
  get fragment(): SectionFragment | void {
    return this.data.sectionFragment;
  }

  /**
   * Children of link model in TOC
   */
  set items(items: Array<ContentLinkModel>) {
    this._items = items;
  }

  /**
   */
  get items(): Array<ContentLinkModel> {
    return this._items;
  }
}
